"use client";

import { JSX, useEffect, useRef } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import { useSvgId } from "@/lib/hooks/use-svg-id";
import {
  useHoverSelect,
  useLegendHoverSelect,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { importsTokenScope } from "./imports-token-scope";

export type SegmentedProgressSegment = {
  id: string;
  name: string;
  weight: number;
  progress: number;
  color: string;
};

export type SegmentedProgressData = {
  segments: SegmentedProgressSegment[];
};

const DEFAULT_DATA: SegmentedProgressData = {
  segments: [
    { id: "discovery", name: "Discovery", weight: 15, progress: 100, color: "var(--chart-1)" },
    { id: "design", name: "Design", weight: 25, progress: 100, color: "var(--chart-2)" },
    { id: "build", name: "Build", weight: 35, progress: 68, color: "var(--chart-3)" },
    { id: "qa", name: "QA", weight: 15, progress: 0, color: "var(--chart-4)" },
    { id: "ship", name: "Ship", weight: 10, progress: 0, color: "var(--chart-5)" },
  ],
};

export default function ModuleSegmentedProgress({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: SegmentedProgressData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const segments = data.segments ?? [];
  const legend = useLegendHoverSelect();
  const hoverGesture = useHoverGesture();
  const maskId = useSvgId("sp-mask");
  const totalWeight = segments.reduce((a, s) => a + s.weight, 0) || 1;

  const legendItems = segments.map((s) => ({
    id: s.id,
    label: stripInlineMarkdown(s.name),
    color: s.color,
    value: `${s.progress}%`,
    percentage: s.weight,
  }));

  const legendIndexForSegment = (segmentId: string) =>
    legendItems.findIndex((li) => li.id === segmentId);

  // The legend is this module's selection: one item per segment.
  const segment = useHoverSelect<string>({
    control: {
      selected:
        legend.effective !== null
          ? legendItems[legend.effective]?.id ?? null
          : null,
      select: (id, source) => legend.select(legendIndexForSegment(id), source),
      setSelected: (id) =>
        legend.setSelected(id === null ? null : legendIndexForSegment(id)),
    },
  });
  const effective = segment.effective;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<string, SVGGElement | null>>({});
  const activeSegment = effective
    ? segments.find((s) => s.id === effective) ?? null
    : null;
  const activeIndex = legend.selected;
  const activeSegmentId =
    activeIndex === null ? null : legendItems[activeIndex]?.id ?? null;
  useEffect(() => {
    if (!activeSegmentId) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const target = container.querySelector(
      `[data-part-index="${activeSegmentId}"]`,
    );
    scrollIntoViewX(container, target, { once: false });
  }, [activeSegmentId]);

  const W = 760;
  const H = 130;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [segments]);
  const padX = 24;
  const chartW = W - padX * 2;
  const barY = 50;
  const barH = 32;
  const gap = 4;
  const totalGaps = Math.max(0, segments.length - 1) * gap;
  const segmentTotalW = chartW - totalGaps;

  let accX = padX;
  const layout = segments.map((s) => {
    const w = (s.weight / totalWeight) * segmentTotalW;
    const entry = { ...s, x: accX, w };
    accX += w + gap;
    return entry;
  });

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id={maskId} />
          <g mask={`url(#${maskId})`}>
            <line
              x1={0}
              x2={W}
              y1={barY - 16}
              y2={barY - 16}
              stroke="var(--grid)"
              strokeDasharray="4 4"
              strokeWidth="1"
              fill="none"
            />
            <line
              x1={0}
              x2={W}
              y1={barY + barH + 16}
              y2={barY + barH + 16}
              stroke="var(--grid)"
              strokeDasharray="4 4"
              strokeWidth="1"
              fill="none"
            />
          </g>

          {layout.map((s) => {
            const isHovered = effective === s.id;
            const dim = effective !== null && !isHovered;
            const progressW = s.w * (s.progress / 100);

            return (
              <g
                key={s.id}
                ref={(el) => {
                  anchorRefs.current[s.id] = el;
                }}
                data-module-type="segmented-progress"
                data-part-index={s.id}
                data-part-label={s.name}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) segment.setHovered(s.id);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) segment.setHovered(null);
                }}
                onClick={(e) =>
                  segment.select(s.id, { kind: "segment", id: s.id, element: e.currentTarget })
                }
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                opacity={dim ? 0.4 : 1}
              >
                <rect
                  x={s.x}
                  y={barY}
                  width={s.w}
                  height={barH}
                  rx="4"
                  fill="var(--card)"
                  stroke={s.color}
                  strokeWidth={isHovered ? 1.5 : 1}
                  strokeOpacity={s.progress === 0 ? 0.5 : 1}
                  strokeDasharray={s.progress === 0 ? "3 3" : "0"}
                />
                {s.progress > 0 && (
                  <rect
                    x={s.x}
                    y={barY}
                    width={progressW}
                    height={barH}
                    rx="4"
                    fill={s.color}
                    opacity={s.progress === 100 ? 0.9 : 1}
                    style={{ transition: "width 0.4s" }}
                  />
                )}
                <text
                  x={s.x + s.w / 2}
                  y={barY - 8}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight={isHovered ? 600 : 500}
                  fill={
                    isHovered ? "var(--foreground)" : "var(--muted-foreground)"
                  }
                  style={{ transition: "fill 0.15s" }}
                >
                  {stripInlineMarkdown(s.name)}
                </text>
                {s.progress > 0 && (
                  <text
                    x={s.x + s.w - 6}
                    y={barY + barH / 2 + 4}
                    textAnchor="end"
                    fontSize="10.5"
                    fill={s.progress >= 20 ? "var(--background)" : "var(--foreground)"}
                    fontWeight="600"
                    style={{
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {s.progress}%
                  </text>
                )}
              </g>
            );
          })}
          {layout.slice(1).map((s, i) => (
            <circle
              key={`ms-${i}`}
              cx={s.x - gap / 2}
              cy={barY + barH / 2}
              r="1.5"
              fill="var(--subtle)"
            />
          ))}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activeSegment !== null}
        anchor={activeSegment ? anchorRefs.current[activeSegment.id] ?? null : null}
        contentKey={activeSegment ? String(activeSegment.id) : undefined}
        title={stripInlineMarkdown(activeSegment?.name) || undefined}
        rows={
          activeSegment
            ? [
                { color: activeSegment.color, label: t("Progress"), value: `${activeSegment.progress}%` },
                { color: activeSegment.color, label: t("Weight"), value: `${activeSegment.weight}%` },
              ]
            : []
        }
      />

      {showLegend && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={legend.hovered}
            onHoverChange={legend.setHovered}
            selectedIndex={legend.selected}
            onSelect={legend.select}
            onLabelRef={legend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue showPercentage />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
